import {
  Add as AddIcon,
  Cake as CakeIcon,
  CalendarMonth as CalendarIcon,
  CardGiftcard as GiftIcon,
  Group as GroupIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  InputAdornment,
  Paper,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { addYears, format, isBefore, isSameDay, setYear } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useGoogleCalendar from '../hooks/useGoogleCalendar.ts';
import { useUser } from '../hooks/useUser.ts';
import { Birthday, deleteBirthday, getBirthdays, updateBirthday } from '../services/api.ts';
import AddBirthdayDialog from './AddBirthdayDialog.tsx';
import CollectionLink from './CollectionLink.tsx';
import GiftDrawer from './GiftDrawer.tsx';

export type BirthdayPerson = {
  id: string;
  name: string;
  birth_date: string;
  gifts?: any[];
  reminder_days?: number[];
  google_calendar_event_id?: string | null;
  emoji?: string;
};

// Define the focus areas for the editor
export type EditorFocusArea = 'name' | 'date' | 'reminders' | 'emoji' | null;

// Helper function to calculate days until next birthday and determine closeness
const getUpcomingBirthdayInfo = (birthDate: string) => {
  const today = new Date();
  // Parse date string (YYYY-MM-DD) in local timezone to avoid UTC conversion
  const [year, month, day] = birthDate.split('-').map(Number);
  const birthDateObj = new Date(year, month - 1, day);
  
  // Set the birth date to the current year
  let nextBirthday = setYear(birthDateObj, today.getFullYear());
  
  // If the birthday has already occurred this year, set it to next year
  if (isBefore(nextBirthday, today) && !isSameDay(nextBirthday, today)) {
    nextBirthday = addYears(nextBirthday, 1);
  }
  
  // Calculate days until next birthday
  const daysUntil = Math.ceil((nextBirthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  // Determine closeness category and styling
  let closenessBadge: { 
    label: string; 
    color: string; 
    bgColor: string;
    showBadge: boolean;
    hasBlueBorder: boolean;
  } | null = null;
  
  // Set the properties based on days remaining
  if (daysUntil === 0) {
    closenessBadge = {
      label: "Today!",
      color: "#e53935", // red
      bgColor: "rgba(229, 57, 53, 0.15)",
      showBadge: true,
      hasBlueBorder: true
    };
  } else if (daysUntil <= 7) {
    closenessBadge = {
      label: "Soon",
      color: "#f57c00", // orange
      bgColor: "rgba(245, 124, 0, 0.12)",
      showBadge: true,
      hasBlueBorder: true
    };
  } else if (daysUntil <= 30) {
    closenessBadge = {
      label: "This month",
      color: "#43a047", // green
      bgColor: "rgba(67, 160, 71, 0.1)",
      showBadge: false,
      hasBlueBorder: daysUntil <= 14
    };
  } else {
    closenessBadge = {
      label: "", // no label for far-away birthdays
      color: "#9e9e9e", // gray
      bgColor: "rgba(158, 158, 158, 0.06)",
      showBadge: false,
      hasBlueBorder: false
    };
  }
  
  return {
    daysUntil,
    nextBirthday,
    closenessBadge
  };
};

// Birthday skeleton component for loading state
const BirthdaySkeleton = () => {
  const theme = useTheme();
  
  return (
    <Box sx={{ width: { xs: '100%', md: 'calc(50% - 12px)' } }}>
      <Card 
        sx={{ 
          height: '100%',
          borderRadius: 3,
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.08)',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #FEFEFE 100%)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}
      >
        <CardContent sx={{ 
          padding: { xs: '16px', md: '20px' },
          '&:last-child': { paddingBottom: { xs: '16px', md: '20px' } } 
        }}>
          <Box sx={{ mb: { xs: 1.5, md: 2.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Skeleton variant="circular" width={28} height={28} sx={{ mr: 1.5 }} />
              <Skeleton variant="text" width={140} height={36} />
            </Box>
            <Skeleton variant="text" width={120} height={28} />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Skeleton 
              variant="text" 
              width={80} 
              height={28} 
            />
            <Skeleton 
              variant="text" 
              width={80} 
              height={28} 
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

type BirthdayListProps = {
  initialSelectedPersonId?: string | null;
};

const BirthdayList = ({ initialSelectedPersonId }: BirthdayListProps) => {
  const { data: user } = useUser();
  const [birthdays, setBirthdays] = useState<Birthday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBirthday, setEditingBirthday] = useState<Birthday | null>(null);
  const [giftDrawerOpen, setGiftDrawerOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<BirthdayPerson | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editorFocus, setEditorFocus] = useState<EditorFocusArea>(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [collectionLinkOpen, setCollectionLinkOpen] = useState(false);
  // Add state for tracking sync loading by birthday ID
  const [syncLoading, setSyncLoading] = useState<Record<string, boolean>>({});
  
  // Add ref for search input to maintain focus
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Use callback for search change to prevent unnecessary re-renders
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);
  
  // Get Google Calendar connection status
  const { syncEvent, removeEvent, checkGoogleConnection } = useGoogleCalendar();
  
  // Theme for responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Create the search controls UI - memoize to prevent recreating on every render
  const searchControlsElement = useMemo(() => (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        gap: 2,
        width: '100%',
        mb: isMobile ? 0 : 3
      }}
    >
      <TextField
        placeholder="Search"
        value={searchQuery}
        onChange={handleSearchChange}
        sx={{ 
          flexGrow: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius: '0.375rem',
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            height: isMobile ? '40px' : 'auto',
          }
        }}
        size={isMobile ? "small" : "medium"}
        inputRef={searchInputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="primary" fontSize={isMobile ? "small" : "medium"} />
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {/* Add button */}
        <Button 
          variant="outlined" 
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            whiteSpace: 'nowrap',
            minWidth: { xs: '36px', sm: 'auto' },
            borderRadius: '0.375rem',
            py: isMobile ? 0.7 : 1.2,
            px: isMobile ? 1.5 : 2,
            height: isMobile ? '40px' : '48px',
            borderWidth: 1,
          }}
        >
          <Box>Add</Box>
        </Button>
        
        {/* Collect button */}
        <Button 
          variant="contained" 
          color="primary"
          startIcon={<GroupIcon />}
          onClick={() => setCollectionLinkOpen(true)}
          sx={{
            whiteSpace: 'nowrap',
            minWidth: { xs: '60px', sm: 'auto' },
            borderRadius: '0.375rem',
            py: isMobile ? 0.7 : 1.2,
            px: isMobile ? 1.5 : 2,
            height: isMobile ? '40px' : '48px',
            backgroundColor: theme.palette.primary.main,
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
        Collect
        </Button>
      </Box>
    </Box>
  ), [searchQuery, handleSearchChange, theme, isMobile, setCollectionLinkOpen]);

  // Fetch birthdays function for manual refresh
  const fetchBirthdays = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const data = await getBirthdays();
      setBirthdays(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error("Error fetching birthdays:", err);
      setBirthdays([]);
      setError(err instanceof Error ? err : new Error('Error fetching birthdays'));
    } finally {
      setIsLoading(false);
    }
  };

  // Load birthdays when user changes
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (!user) {
        if (isMounted) {
          setIsLoading(false);
          setBirthdays([]);
        }
        return;
      }
      
      setIsLoading(true);
      
      try {
        const data = await getBirthdays();
        // Only update state if component is still mounted
        if (isMounted) {
          setBirthdays(Array.isArray(data) ? data : []);
          setError(null);
        }
      } catch (err) {
        console.error("Error fetching birthdays:", err);
        if (isMounted) {
          setBirthdays([]);
          setError(err instanceof Error ? err : new Error('Error fetching birthdays'));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    loadData();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [user]);

  // Open gift drawer for the initially selected person
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSelectedPersonId || birthdays.length === 0 || isLoading) {
      return;
    }
    
    const person = birthdays.find(b => b.id === initialSelectedPersonId);
    if (person) {
      handleOpenGiftDrawer(person);
    }
  }, [initialSelectedPersonId, birthdays, isLoading]);

  const handleOpenDialog = (birthday?: Birthday, focusArea: EditorFocusArea = null) => {
    if (birthday) {
      setEditingBirthday(birthday);
    } else {
      setEditingBirthday(null);
    }
    setEditorFocus(focusArea);
    setDialogOpen(true);
  };

  const handleDialogChange = async (open: boolean, wasSaved = false) => {
    setDialogOpen(open);
    if (!open) {
      // Only refresh data if a birthday was saved or deleted
      if (wasSaved) {
        fetchBirthdays();
      }
      setEditorFocus(null);
    }
  };

  // Update Google Calendar event when a birthday is edited
  const updateCalendarEvent = async (birthday: Birthday) => {
    if (!birthday.google_calendar_event_id) {
      return;
    }

    try {
      // Set loading state for just this button
      setSyncLoading(prev => ({ ...prev, [birthday.id]: true }));
      
      // Prepare birthday event data for calendar update
      // Parse date in local timezone to avoid UTC conversion
      const [year, month, day] = birthday.birth_date.split('-').map(Number);
      const birthdayEvent = {
        name: birthday.name,
        date: new Date(year, month - 1, day),
        reminderDays: birthday.reminder_days || [],
        appEventId: birthday.id,
        emoji: birthday.emoji || '🎂'
      };
      
      // Sync with Google Calendar (this will update the existing event)
      await syncEvent(birthdayEvent);
      
      setSnackbarMessage('Event updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating Google Calendar event:', error);
      setSnackbarMessage('Failed to update Google Calendar event');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSyncLoading(prev => ({ ...prev, [birthday.id]: false }));
    }
  };

  // Handle delete birthday
  const handleDeleteBirthday = async (id: string) => {
    // Optimistically update UI by removing birthday from state
    const birthdayToDelete = birthdays.find(b => b.id === id);
    const updatedBirthdays = birthdays.filter(birthday => birthday.id !== id);
    setBirthdays(updatedBirthdays);
    
    // Show success toast
    setSnackbarMessage('Birthday deleted');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    
    // Set loading state for just this birthday
    setSyncLoading(prev => ({ ...prev, [id]: true }));
    
    try {
      // If the birthday has a Google Calendar event, remove it
      if (birthdayToDelete?.google_calendar_event_id) {
        try {
          await removeEvent(id);
        } catch (syncErr) {
          console.error('Error removing calendar event:', syncErr);
          // Continue with deletion even if calendar removal fails
        }
      }
      
      await deleteBirthday(id);
      // No need to fetch birthdays again since we've already updated the UI
    } catch (err) {
      // If there's an error, revert the optimistic update
      if (birthdayToDelete) {
        setBirthdays(prevBirthdays => [...prevBirthdays, birthdayToDelete].sort((a, b) => {
          return getUpcomingBirthdayInfo(a.birth_date).daysUntil - getUpcomingBirthdayInfo(b.birth_date).daysUntil;
        }));
      } else {
        // Fallback to refreshing the data if we can't find the deleted birthday
        await fetchBirthdays();
      }
      
      setSnackbarMessage('Failed to delete birthday');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setError(err instanceof Error ? err : new Error('Error deleting birthday'));
    } finally {
      setSyncLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleOpenGiftDrawer = (birthday: BirthdayPerson) => {
    setSelectedPerson(birthday);
    setGiftDrawerOpen(true);
  };

  // Filter birthdays based on search query
  const filteredBirthdays = Array.isArray(birthdays) 
    ? birthdays
        .filter(birthday => 
          birthday && birthday.name && birthday.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => {
          // Sort by the next upcoming birthday
          return getUpcomingBirthdayInfo(a.birth_date).daysUntil - getUpcomingBirthdayInfo(b.birth_date).daysUntil;
        })
    : [];

  // Handle direct calendar sync
  const handleCalendarSync = async (e: React.MouseEvent, birthday: Birthday) => {
    e.stopPropagation();
    
    try {
      // Set loading state for just this button
      setSyncLoading(prev => ({ ...prev, [birthday.id]: true }));
      
      // Prepare birthday event data
      // Parse date in local timezone to avoid UTC conversion
      const [year2, month2, day2] = birthday.birth_date.split('-').map(Number);
      const birthdayEvent = {
        name: birthday.name,
        date: new Date(year2, month2 - 1, day2),
        reminderDays: birthday.reminder_days || [],
        appEventId: birthday.id,
        emoji: birthday.emoji || '🎂'
      };
      
      // Optimistically update UI to show calendar event as synced
      const updatedBirthday = {
        ...birthday,
        // We'll update this with the real ID after the API call
        google_calendar_event_id: 'pending' 
      };
      
      // Update local state
      setBirthdays(prevBirthdays => 
        prevBirthdays.map(b => 
          b.id === birthday.id ? updatedBirthday : b
        )
      );
      
      try {
        // Sync with Google Calendar
        const eventId = await syncEvent(birthdayEvent);
        
        // After successful sync, update with real event ID
        updatedBirthday.google_calendar_event_id = eventId;
        setBirthdays(prevBirthdays => 
          prevBirthdays.map(b => 
            b.id === birthday.id ? updatedBirthday : b
          )
        );
        
        // Update birthday in database with Google Calendar event ID
        await updateBirthday(birthday.id, {
          name: birthday.name,
          birth_date: birthday.birth_date,
          emoji: birthday.emoji,
          google_calendar_event_id: eventId,
          reminder_days: birthday.reminder_days || []
        });
        
        // Show success toast
        setSnackbarMessage('Birthday added to Google Calendar');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (error: any) {
        console.error('Error syncing calendar event:', error);
        
        // If the error message indicates we're redirecting for permissions, don't show an error
        if (error.message && error.message.includes('Redirecting for calendar permissions')) {
          setSnackbarMessage('Redirecting to Google for calendar permissions...');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          return; // Don't continue execution as we're redirecting
        }
        
        // Revert the optimistic update
        setBirthdays(prevBirthdays => 
          prevBirthdays.map(b => 
            b.id === birthday.id ? birthday : b
          )
        );
        
        setSnackbarMessage('Failed to add to Google Calendar');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setError(error instanceof Error ? error : new Error('Failed to sync calendar event'));
      }
    } catch (error: any) {
      console.error('Outer error syncing calendar event:', error);
      
      // Revert the optimistic update
      setBirthdays(prevBirthdays => 
        prevBirthdays.map(b => 
          b.id === birthday.id ? birthday : b
        )
      );
      
      // If the error message indicates we're redirecting for permissions, don't show an error
      if (error.message && error.message.includes('Redirecting for calendar permissions')) {
        setSnackbarMessage('Redirecting to Google for calendar permissions...');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
        return; // Don't continue execution as we're redirecting
      }
      
      setSnackbarMessage('Failed to add to Google Calendar');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setError(error instanceof Error ? error : new Error('Failed to sync calendar event'));
    } finally {
      setSyncLoading(prev => ({ ...prev, [birthday.id]: false }));
    }
  };

  // Handle unsync calendar event
  const handleUnsyncCalendar = async (e: React.MouseEvent, birthday: Birthday) => {
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to remove this event from your calendar?')) {
      try {
        // Set loading state for just this button
        setSyncLoading(prev => ({ ...prev, [birthday.id]: true }));
        
        // Create an updated birthday without Google Calendar event ID
        const updatedBirthday = { 
          ...birthday,
          google_calendar_event_id: null 
        };
        
        // Optimistically update UI
        setBirthdays(prevBirthdays => 
          prevBirthdays.map(b => 
            b.id === birthday.id ? updatedBirthday : b
          )
        );
        
        try {
          // Call the removeEvent function to delete from Google Calendar
          await removeEvent(birthday.id);
          
          // Update the birthday in database
          await updateBirthday(birthday.id, {
            name: birthday.name,
            birth_date: birthday.birth_date,
            emoji: birthday.emoji,
            google_calendar_event_id: null,
            reminder_days: birthday.reminder_days || []
          });
          
          // Show success toast
          setSnackbarMessage('Birthday removed from Google Calendar');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
        } catch (error: any) {
          console.error('Error unsyncing calendar event:', error);
          
          // If the error message indicates we're redirecting for permissions, don't show an error
          if (error.message && error.message.includes('Redirecting for calendar permissions')) {
            setSnackbarMessage('Redirecting to Google for calendar permissions...');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
            return; // Don't continue execution as we're redirecting
          }
          
          // Revert the optimistic update
          setBirthdays(prevBirthdays => 
            prevBirthdays.map(b => 
              b.id === birthday.id ? birthday : b
            )
          );
          
          setSnackbarMessage('Failed to remove from Google Calendar');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setError(error instanceof Error ? error : new Error('Failed to unsync calendar event'));
        }
      } catch (error: any) {
        console.error('Outer error unsyncing calendar event:', error);
        
        // Revert the optimistic update
        setBirthdays(prevBirthdays => 
          prevBirthdays.map(b => 
            b.id === birthday.id ? birthday : b
          )
        );
        
        // If the error message indicates we're redirecting for permissions, don't show an error
        if (error.message && error.message.includes('Redirecting for calendar permissions')) {
          setSnackbarMessage('Redirecting to Google for calendar permissions...');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          return; // Don't continue execution as we're redirecting
        }
        
        setSnackbarMessage('Failed to remove from Google Calendar');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setError(error instanceof Error ? error : new Error('Failed to unsync calendar event'));
      } finally {
        setSyncLoading(prev => ({ ...prev, [birthday.id]: false }));
      }
    }
  };

  // Handle toast close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (error) return <Typography color="error">{error.message}</Typography>;

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        mt: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2, md: 3 },
        pb: isMobile ? 10 : 4, // Add padding at bottom for mobile to accommodate fixed controls
        position: 'relative'
      }}
    >
      {/* Only render the controls at the top for non-mobile */}
      {!isMobile && (
        <Box sx={{ mb: 3 }}>
          {searchControlsElement}
        </Box>
      )}
      
      {isLoading && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 3 }}>
          {[...Array(3)].map((_, index) => (
            <BirthdaySkeleton key={index} />
          ))}
        </Box>
      )}

      {!isLoading && birthdays.length > 0 && filteredBirthdays.length === 0 && (
        <Paper
          elevation={2}
          sx={{ 
            py: 6, 
            textAlign: 'center', 
            borderRadius: 3,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)'
          }}
        >
          <Typography variant="h3" color="text.secondary" sx={{ mb: 2 }}>
            No birthdays match your search
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Try a different search term or clear your search
          </Typography>
        </Paper>
      )}

      {!isLoading && birthdays.length === 0 && (
        <Paper
          elevation={3}
          sx={{ 
            p: 5, 
            textAlign: 'center', 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #FFFFFF 0%, #FEFEFE 100%)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative elements */}
          <Box 
            sx={{ 
              position: 'absolute', 
              top: -30, 
              right: -30, 
              width: 180, 
              height: 180, 
              borderRadius: '50%', 
              background: alpha(theme.palette.primary.light, 0.05),
              zIndex: 0
            }} 
          />
          <Box 
            sx={{ 
              position: 'absolute', 
              bottom: -40, 
              left: -40, 
              width: 200, 
              height: 200, 
              borderRadius: '50%', 
              background: alpha(theme.palette.secondary.light, 0.05),
              zIndex: 0
            }} 
          />
          
          <Box 
            sx={{ 
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
              zIndex: 1,
              position: 'relative'
            }}
          >
            <CakeIcon sx={{ fontSize: 60, color: theme.palette.primary.main }} />
          </Box>
          
          <Box>
            <Typography variant="h2" color="primary.main" sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
              No birthdays yet!
            </Typography>
          </Box>
        </Paper>
      )}

      {!isLoading && filteredBirthdays.length > 0 && (
        <Box 
          sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            justifyContent: 'space-between', 
            gap: { xs: 2, sm: 3 } // Reduce gap on mobile
          }}
        >
          {filteredBirthdays.map((birthday) => (
            <Box 
              key={birthday.id} 
              sx={{ width: { xs: '100%', md: 'calc(50% - 12px)' } }}
            >
              <Card 
                sx={{ 
                  height: '100%', 
                  cursor: 'pointer',
                  position: 'relative',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.06)',
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #FEFEFE 100%)',
                  border: `1px solid ${getUpcomingBirthdayInfo(birthday.birth_date).closenessBadge?.hasBlueBorder ? 
                    alpha(theme.palette.primary.main, 0.3) : 
                    alpha(theme.palette.primary.main, 0.1)
                  }`,
                }}
                onClick={() => handleOpenDialog(birthday)}
              >
                {/* Decorative accent color - semicircle */}
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    right: 0, 
                    width: 100, 
                    height: 100, 
                    borderRadius: '0 0 0 100%', 
                    background: alpha(theme.palette.primary.light, 0.05),
                    zIndex: 0
                  }} 
                />
                
                {/* Birthday closeness indicator - only show for upcoming birthdays */}
                {getUpcomingBirthdayInfo(birthday.birth_date).closenessBadge?.showBadge && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      zIndex: 2,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '1rem',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: getUpcomingBirthdayInfo(birthday.birth_date).closenessBadge?.color || 'inherit',
                      backgroundColor: getUpcomingBirthdayInfo(birthday.birth_date).closenessBadge?.bgColor || 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    {getUpcomingBirthdayInfo(birthday.birth_date).closenessBadge?.label}
                  </Box>
                )}
                
                <CardContent sx={{ 
                  padding: { xs: '16px', md: '20px' },
                  '&:last-child': { paddingBottom: { xs: '16px', md: '20px' } },
                  position: 'relative',
                  zIndex: 1
                }}>
                  <Box sx={{ mb: { xs: 1.5, md: 2.5 } }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        cursor: 'pointer',
                        mb: 0.8,
                        pr: 5 // Add right padding to avoid overlap with the closeness badge
                      }}
                    >
                      <Box 
                        component="span"
                        className="emoji-container" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDialog(birthday, 'emoji');
                        }}
                        sx={{ 
                          fontSize: { xs: '1.5rem', md: '1.8rem' },
                          mr: 1.5,
                          cursor: 'pointer',
                        }}
                      >
                        {birthday.emoji || '🎂'}
                      </Box>
                      <Typography 
                        variant="h6" 
                        component="h2"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDialog(birthday, 'name');
                        }}
                        sx={{ 
                          cursor: 'pointer',
                          fontSize: { xs: '1.1rem', md: '1.3rem' },
                          fontWeight: 600,
                        }}
                      >
                        {birthday.name}
                      </Typography>
                    </Box>
                    
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDialog(birthday, 'date');
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'text.secondary',
                        cursor: 'pointer',
                        width: 'fit-content',
                      }}
                    >
                      <Typography 
                        variant="body1"
                        sx={{ 
                          fontWeight: 500,
                          lineHeight: 1.2,
                          fontSize: { xs: '0.95rem', md: '1.05rem' }
                        }}
                      >
                        {(() => {
                          const [y, m, d] = birthday.birth_date.split('-').map(Number);
                          return format(new Date(y, m - 1, d), 'MMMM d');
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {birthday.google_calendar_event_id ? (
                      <Tooltip title="Unsync from Google Calendar">
                        <Button
                          size="small"
                          variant="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnsyncCalendar(e, birthday);
                          }}
                          disabled={syncLoading[birthday.id]}
                          sx={{ 
                            fontSize: { xs: '0.85rem', md: '0.9rem' },
                            fontWeight: 'medium',
                            color: theme.palette.text.secondary,
                            opacity: 0.8,
                            padding: { xs: '6px 10px', md: '6px 12px' },
                            minWidth: 0,
                            '&:hover': {
                              opacity: 1,
                              backgroundColor: 'transparent'
                            }
                          }}
                        >
                          {syncLoading[birthday.id] ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  mr: 0.5,
                                  borderRadius: '50%',
                                  border: '2px solid',
                                  borderColor: 'text.secondary',
                                  borderTopColor: 'transparent',
                                  animation: 'spin 1s linear infinite',
                                  '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                  }
                                }}
                              />
                              <span>Syncing</span>
                            </Box>
                          ) : (
                            'Unsync'
                          )}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button
                        startIcon={syncLoading[birthday.id] ? null : <CalendarIcon fontSize="small" />}
                        onClick={(e) => handleCalendarSync(e, birthday)}
                        size="small"
                        variant="text"
                        disabled={syncLoading[birthday.id]}
                        sx={{ 
                          padding: { xs: '4px 10px', md: '6px 12px' },
                          fontSize: { xs: '0.85rem', md: '0.9rem' },
                          borderRadius: '0.375rem',
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            color: 'primary.main'
                          }
                        }}
                      >
                        {syncLoading[birthday.id] ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 14,
                                height: 14,
                                mr: 0.5,
                                borderRadius: '50%',
                                border: '2px solid',
                                borderColor: 'text.secondary',
                                borderTopColor: 'transparent',
                                animation: 'spin 1s linear infinite',
                                '@keyframes spin': {
                                  '0%': { transform: 'rotate(0deg)' },
                                  '100%': { transform: 'rotate(360deg)' }
                                }
                              }}
                            />
                            <span>Unsyncing</span>
                          </Box>
                        ) : (
                          'Sync'
                        )}
                      </Button>
                    )}

                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenGiftDrawer(birthday);
                      }}
                      size="small"
                      variant="text"
                      color="primary"
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '0.75rem',
                        padding: { xs: '6px 16px', md: '8px 16px' },
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        fontWeight: '500',
                        color: theme.palette.primary.main,
                      }}
                    >
                      <GiftIcon 
                        sx={{ 
                          fontSize: '1rem', 
                          mr: 0.8,
                          color: 'inherit'
                        }} 
                      />
                      <span>
                        {birthday.gifts && birthday.gifts.filter(gift => !gift.is_purchased).length > 0 
                          ? 'Gifts' 
                          : 'Add Gifts'}
                      </span>
                      {birthday.gifts && birthday.gifts.filter(gift => !gift.is_purchased).length > 0 && (
                        <Box
                          component="span"
                          sx={{
                            ml: 0.8,
                            fontSize: '0.7rem',
                            backgroundColor: alpha(theme.palette.primary.main, 0.15),
                            color: theme.palette.primary.main,
                            borderRadius: '50%',
                            width: 18,
                            height: 18,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold'
                          }}
                        >
                          {birthday.gifts.filter(gift => !gift.is_purchased).length}
                        </Box>
                      )}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      {/* Use the advanced AddBirthdayDialog component */}
      <AddBirthdayDialog
        open={dialogOpen}
        onOpenChange={handleDialogChange}
        person={editingBirthday}
        initialFocus={editorFocus}
        onCalendarEventUpdate={updateCalendarEvent}
        onDeleteBirthday={handleDeleteBirthday}
      />

      {/* Gift drawer component */}
      <GiftDrawer 
        open={giftDrawerOpen}
        onOpenChange={setGiftDrawerOpen}
        person={selectedPerson}
      />

      {/* Collection Link Dialog */}
      <CollectionLink
        open={collectionLinkOpen}
        onClose={() => setCollectionLinkOpen(false)}
      />
      
      {/* Toast notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: 3 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Fixed bottom controls for mobile */}
      {isMobile && (
        <Box 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            p: 1.5,
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            boxShadow: '0px -2px 6px rgba(0,0,0,0.05)',
            zIndex: 10
          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              gap: 2,
              width: '100%'
            }}
          >
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ 
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.375rem',
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  height: '40px',
                }
              }}
              size="small"
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {/* Add button */}
              <Button 
                variant="outlined" 
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  whiteSpace: 'nowrap',
                  minWidth: '36px',
                  borderRadius: '0.375rem',
                  py: 0.7,
                  px: 1.5,
                  height: '40px',
                  borderWidth: 2,
                }}
              >
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>Add</Box>
              </Button>
              
              {/* Collect button */}
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<GroupIcon />}
                onClick={() => setCollectionLinkOpen(true)}
                sx={{
                  whiteSpace: 'nowrap',
                  minWidth: '60px',
                  borderRadius: '0.375rem',
                  py: 0.7,
                  px: 1.5,
                  height: '40px',
                  backgroundColor: theme.palette.primary.main,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                }}
              >
                Collect
              </Button>
            </Box>
          </Box>
        </Box>
      )}

    </Container>
  );
};

export default BirthdayList;

